/**
 * 품질 이력 분석 페이지
 * - 기간·품목(차종/품명/품번)·센서데이터(범위) 필터로 날짜별 생산/NG 통계 차트 표시
 * - statsDatas API 호출 후 BarChart로 OK/NG 스택 막대 시각화
 */
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { components } from 'react-select';
import { useOutletContext } from 'react-router';
import moment from 'moment';
import axios from 'axios';
import "react-datepicker/dist/react-datepicker.css";
import { CustomDatePickerStart, CustomDatePickerEnd } from '../../components/CustomDatePicker';
import Select from 'react-select';
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis, Legend, LabelList, ReferenceLine, Tooltip, Label } from 'recharts';
import resetIconImg from '../../../images/resetIcon.png';
import filledArrowIconImg from '../../../images/filledArrowIcon.png';
import { API_URL } from '../../../config/constants';
import NoData from '../../components/NoData';
import './summ_data.scss';
import { getModelTypes, getModelNames, getModelNos, getSensorLabels } from '../../../api/modelApi';

/* ---- 차트 시각화 상수 ---- */
const CHART_HEIGHT = 350;
const BAR_WIDTH_PER_ITEM_PX = 56;                // 날짜 1개당 막대 너비 (가로 스크롤 시 고정폭)
const CHART_MIN_WIDTH_PX = 400;                  // 차트 최소 너비
const MIN_BAR_HEIGHT_FOR_INSIDE_LABEL_PX = 28;   // 이 높이 미만이면 라벨을 막대 바깥에 표시
const MIN_CHART_HEIGHT_FOR_INSIDE_LABEL_PX = 280; // 차트 높이가 이 값 이상이면 막대 높이와 관계없이 라벨을 막대 안에 표시
const MIN_TOTAL_BAR_HEIGHT_FOR_INSIDE_PX = 30;   // 막대 전체 높이가 이 값 미만이면 '그래프가 낮다'고 보고 총 생산을 막대 밖(NG 위) 파란색으로 표시
const COUNT_LABEL_OUTSIDE_OFFSET_PX = 36;        // 총 생산 라벨을 막대 위로 띄우는 거리 (NG 라벨 위에 오도록)
const NG_LABEL_ABOVE_TOTAL_PX = 10;              // 총 생산이 차트 밖에 있을 때 NG가 총 생산 아래에 오도록
const NG_BAR_VERY_THIN_PX = 6;                  // 이 미만이면 총 생산이 막대 안에 있는 걸로 간주 (NG만 막대 바로 위)
const XAXIS_MAX_VISIBLE_TICKS = 10;              // 한 번에 보여줄 X축 tick 최대 개수(디자인 기준)
const XAXIS_TICK_FONT_SIZE = 12;
const BAR_CATEGORY_GAP_PERCENT = 8;         // 막대 군 간격 (%)
const BAR_GAP_PERCENT = 4;                  // OK/NG 막대 간 간격 (%)
const LABEL_LIMIT_THRESHOLD = 20;

const OPTION_ALL = { value: '', label: '전체 선택' };

/** 툴팁에 일자, 총 생산량, OK, NG 표시 */
function ChartTooltipContent({ active, payload, label }) {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0]?.payload;
    const totalCount = row && (typeof row.count === 'number' ? row.count : (Number(row.okCount) || 0) + (Number(row.ngCount) || 0));
    const dateStr = label && typeof label === 'string' ? label.split('T')[0].replace(/-/g, '.') : label;
    return (
        <div className="chartTooltip" style={{ fontFamily: 'NanumSquare, sans-serif', padding: '10px 12px', background: '#fff', border: '1px solid #ddd', borderRadius: 6 }}>
            <div style={{ marginBottom: 6, fontWeight: 600 }}>일자: {dateStr}</div>
            <div>총 생산량: {typeof totalCount === 'number' ? totalCount.toLocaleString() : '-'}개</div>
            {payload.map((entry) => (
                <div key={entry.dataKey} style={{ color: entry.color }}>
                    {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}개
                </div>
            ))}
        </div>
    );
}

/** Recharts BarChart 라벨: 파란(OK) 막대 안에 '총 생산' + 개수 표시, 막대가 작으면 위쪽에 표시 */
// function CountBarLabel({ x, y, width, height, value }) {
//     const count = Number(value) || 0;
//     if (count <= 0) return null;
//     const showInside = height >= MIN_BAR_HEIGHT_FOR_INSIDE_LABEL_PX;
//     const yPos = showInside ? y + height / 2 : y - COUNT_LABEL_OUTSIDE_OFFSET_PX;
//     const labelFill = showInside ? '#FFF' : '#0A2B83';
//     return (
//         <text
//             x={x + width / 2}
//             y={yPos}
//             textAnchor="middle"
//             fill={labelFill}
//             fontSize={12}
//             fontFamily="LeferiPointWhite"
//         >
//             <tspan x={x + width / 2} dy={showInside ? -2 : -2}>
//                 총 생산
//             </tspan>
//             <tspan x={x + width / 2} dy={14}>
//                 {count.toLocaleString()}
//             </tspan>
//         </text>
//     );
// }

/** Recharts BarChart 라벨: 세그먼트 최상단에 '총 생산' + 개수 표시, NG 세그먼트일 때는 추가로 'NG {개수}' 표시 (한 컴포넌트에서 통합 관리) */
function TotalLabelOnTopSegment({ x, y, width, height, payload, value, chartHeight = 0 }) {
    // Recharts filterProps가 SVG 외 prop(payload 등)을 제거해서 payload가 undefined로 옴 → 상위에서 chartData[index]로 주입
    const rawCount = payload?.count ?? (payload?.okCount != null && payload?.ngCount != null ? (Number(payload.okCount) + Number(payload.ngCount)) : undefined);
    const count = Number(rawCount) || 0;
    const ngCount = (Number(payload?.ngCount) ?? Number(value)) || 0; // value는 NG Bar에서만 ngCount 전달, OK Bar에서는 payload만 사용
    const okCount = Number(payload?.okCount) ?? Math.max(0, count - ngCount);
    if (count <= 0) return null;

    // ✅ 현재 라벨이 NG 조각에 붙어있으면 height는 "NG 높이(px)"
    //    -> OK 높이(px) = NG높이(px) * (okCount / ngCount)
    // ✅ NG=0이면 라벨은 OK 조각에 붙고 height가 곧 OK 높이(px)
    const okHeightPx = ngCount > 0 ? (height * (okCount / ngCount)) : height;

    // ✅ 그래프(막대) 총 높이가 낮으면 총 생산을 막대 밖, NG 라벨 위에 파란색으로 표시
    const totalBarHeightPx = ngCount > 0 ? (height + okHeightPx) : height; //okheightpx는 
    const isBarHeightLow = totalBarHeightPx < MIN_TOTAL_BAR_HEIGHT_FOR_INSIDE_PX; //MIN_TOTAL_BAR_HEIGHT_FOR_INSIDE_PX:80
    const barTallEnough = ngCount > 0 ? (height >= MIN_BAR_HEIGHT_FOR_INSIDE_LABEL_PX) : (okHeightPx >= MIN_BAR_HEIGHT_FOR_INSIDE_LABEL_PX); //MIN_BAR_HEIGHT_FOR_INSIDE_LABEL_PX:28
    const showInside = !isBarHeightLow && (barTallEnough || chartHeight >= MIN_CHART_HEIGHT_FOR_INSIDE_LABEL_PX); //MIN_CHART_HEIGHT_FOR_INSIDE_LABEL_PX:280

    // ✅ 차트 높이가 충분할 때: OK 높이 vs NG 높이 비교해서 더 큰 세그먼트 가운데에 라벨 배치
    const okSegmentBiggerOrEqual = okHeightPx >= height;
    const yPos = showInside
      ? (ngCount > 0 && okCount > 0
          ? (okSegmentBiggerOrEqual ? (y + height + okHeightPx / 2) : (y + height / 2))
          : (y + height / 2))
      : (y - COUNT_LABEL_OUTSIDE_OFFSET_PX);

    const fill = "#000000";
    const totalInsideBar = height < NG_BAR_VERY_THIN_PX;
    const ngOffsetPx = totalInsideBar ? 6 : NG_LABEL_ABOVE_TOTAL_PX;
    const boxW = 40;
    const boxH = 34;
    const boxX = x+width /2 - boxW / 2;
    const boxY = yPos - 15;

    return (
        <g>
          {/* 막대 안/밖 공통: 반투명 흰 박스로 가독성 확보 */}
          <rect
            x={boxX}
            y={boxY}
            width={boxW}
            height={boxH}
            rx={4}
            ry={4}
            fill="rgba(255,255,255,0.7)"
          />
    
          {/* 총 생산 라벨 */}
          <text
            x={x + width / 2}
            y={yPos}
            textAnchor="middle"
            fill={fill}
            fontSize={12}
            fontFamily="NanumSquare"
            className={showInside ? "labelWhiteText" : ""}
          >
            <tspan x={x + width / 2} dy={-2}>총 생산</tspan>
            <tspan x={x + width / 2} dy={14}>{count.toLocaleString()}</tspan>
          </text>
    
          {/* NG 라벨 */}
          {ngCount > 0 && (
            <text
              x={x + width / 2}
              y={y - ngOffsetPx}
              textAnchor="middle"
              fill="#C04F15"
              fontSize={12}
              fontFamily="NanumSquare"
            >
              {`NG ${ngCount.toLocaleString()}`}
            </text>
          )}
        </g>
      );
  }

function SumDataStatistic() {
    const { selSumDsId } = useOutletContext() || {};
    const processDisplayName = 'summ_data';

    /* ---- 기간·품목 필터 state ---- */
    /* 오늘 포함 최근 3일 기본값 */
    const [startDate, setStartDate] = useState(moment().subtract(2, 'days').startOf('day').toDate());
    const [endDate, setEndDate] = useState(moment().toDate());
    const [modelTypes, setModelTypes] = useState([]);           // 차종 목록 (API)
    const [modelNames, setModelNames] = useState([]);           // 품명 목록 (API)
    const [modelNos, setModelNos] = useState([]);               // 품번 목록 (API)
    const [selectedModelType, setSelectedModelType] = useState(OPTION_ALL);
    const [selectedModelName, setSelectedModelName] = useState(OPTION_ALL);
    const [selectedModelNo, setSelectedModelNo] = useState(OPTION_ALL);
    const [sensor, setSensor] = useState([]);                   // 센서데이터(범위) 목록 (getSensorLabels)
    const [selectedSensor, setSelectedSensor] = useState({ value: 'all', label: '전체 선택' }); // 선택한 센서 → scope로 변환해 API 전달
    const [selectPeriod, setSelectPeriod] = useState('recent3Days');
    const [scope, setScope] = useState("all");                  // all | 원자재 | 하중 | 치수 | dec0~ (백엔드 sDecision 필터용)

    /* ---- 차트 데이터 state ---- */
    const [total, setTotal] = useState([]);                     // statsDatas API 응답 dateData
    const [isLoadingChart, setIsLoadingChart] = useState(false);

    /** statsDatas API 호출 → 날짜별 count/ngCount를 total state에 저장. 차종=전체면 날짜만, 차종 선택 시 해당 차종(+ 품명/품번 선택 시) 필터 */
    const fetchBarData = useCallback(async () => {
        if (!selSumDsId || selSumDsId < 0) {
            setTotal([]);
            return;
        }
        const formatDate = (date) => {
            if (!date) return moment().format('YYYY-MM-DD');
            const d = moment(date);
            if (!d.isValid()) return moment().format('YYYY-MM-DD');
            return d.format('YYYY-MM-DD');
        };
        const modelTypeVal = selectedModelType != null && typeof selectedModelType === 'object' && 'value' in selectedModelType ? selectedModelType.value : selectedModelType;
        const modelNameVal = selectedModelName != null && typeof selectedModelName === 'object' && 'value' in selectedModelName ? selectedModelName.value : selectedModelName;
        const modelNoVal = selectedModelNo != null && typeof selectedModelNo === 'object' && 'value' in selectedModelNo ? selectedModelNo.value : selectedModelNo;

        const requestBody = {
            sumDsId: selSumDsId,
            startDate: formatDate(startDate),
            endDate: formatDate(endDate),
            scope: scope,
        };
        // 전체가 아닐 때만 해당 필터 추가 (차종=전체면 날짜만, 품명=전체면 차종+날짜, 품번=전체면 차종+품명+날짜)
        if (modelTypeVal != null && String(modelTypeVal).trim() !== '') requestBody.modelType = String(modelTypeVal).trim();
        if (modelNameVal != null && String(modelNameVal).trim() !== '') requestBody.modelName = String(modelNameVal).trim();
        if (modelNoVal != null && String(modelNoVal).trim() !== '') requestBody.modelNo = String(modelNoVal).trim();

        setTotal([]);
        setIsLoadingChart(true);

        try {
            console.log('[차트] requestBody', requestBody);
            const response = await axios.post(`${API_URL}/android/statsDatas`, requestBody, {
                withCredentials: true,
            });

            let data = response?.data;
            console.log('[resp] scope=', scope, 'data=', data);
            console.log('[resp] scope=', scope, 'first dateData=', data?.dateData?.[0]);
            console.log(
                '[resp] scope=', scope,
                'sum ng=',
                (data?.dateData ?? []).reduce(
                    (s, r) => s + Number(r.ngCount || 0),
                    0
                )
            );
            if (data == null) {
                setTotal([]);
                return;
            }
            if (typeof data === 'string') data = JSON.parse(data);
            const rawDateData = data?.dateData ?? [];
            const mappedData = rawDateData.map((r) => {
                let dateVal = r.date;
                if (dateVal && typeof dateVal === 'string' && dateVal.indexOf('T') !== -1) {
                    dateVal = dateVal.slice(0, 10);
                }
                const count = Number(r.count) || 0;
                // 백엔드에서 scope에 맞게 계산된 단일 NG 값
                const ngCount = Number(r.ngCount) || 0;
                const okCount = Math.max(0, count - ngCount);

                return {
                    date: dateVal,
                    count,
                    ngCount,
                    okCount,
                };
            });
            setTotal([...mappedData]);
        } catch (err) {
            console.error('[차트] statsDatas API error:', err);
            setTotal([]);
        } finally {
            setIsLoadingChart(false);
        }
    }, [selSumDsId, startDate, endDate, selectedModelType, selectedModelName, selectedModelNo, scope]);

    /** 기간 옵션 (데이터관리 페이지와 동일) */
    const periodOpt = [
        { label: '시간직접입력', value: 'input' },
        { label: '최근 3일', value: 'recent3Days' },
        { label: '최근 1시간', value: '1h' },
        { label: '최근 24시간', value: '24h' },
    ];

    /** selectPeriod 변경 시 startDate/endDate 동기화 (데이터관리와 동일 로직) */
    useEffect(() => {
        switch (selectPeriod) {
            case 'input':
                break;
            case 'recent3Days':
                setStartDate(moment().subtract(2, 'days').startOf('day').toDate());
                setEndDate(moment().toDate());
                break;
            case '1h':
                setStartDate(moment().subtract(1, 'hours').toDate());
                setEndDate(moment().toDate());
                break;
            case '24h':
                setStartDate(moment().subtract(1, 'days').toDate());
                setEndDate(moment().toDate());
                break;
            default:
                setStartDate(moment().toDate());
                setEndDate(moment().toDate());
                break;
        }
    }, [selectPeriod]);

    useEffect(() => {
        fetchBarData();
    }, [fetchBarData]);

    /** 공정 선택 시 차종 목록 로드 */
    useEffect(() => {
        if (!selSumDsId || selSumDsId < 0) return;
        getModelTypes({ sumDsId: selSumDsId })
          .then((data) => {
            const opts = Array.isArray(data) ? data.map((el) => (typeof el === 'string' ? { value: el, label: el } : el)) : [];
            setModelTypes(opts);
            console.log('modelTypes', opts);
          })
          .catch(() => setModelTypes([]));
    }, [selSumDsId]);

    /** 차종 선택 시 품명 목록 로드 (차종=전체면 로드 안 함) */
    useEffect(() => {
        if (!selSumDsId || selSumDsId < 0 || !selectedModelType || selectedModelType.value === '') {
            setModelNames([]);
            return;
        }
        getModelNames({ sumDsId: selSumDsId, modelType: selectedModelType })
          .then((data) => {
            const opts = Array.isArray(data) ? data.map((el) => (typeof el === 'string' ? { value: el, label: el } : el)) : [];
            setModelNames(opts);
          })
          .catch(() => setModelNames([]));
          console.log('modelNames', modelNames);
    }, [selSumDsId, selectedModelType]);

    /** 품명 선택 시 품번 목록 로드 (품명=전체면 로드 안 함) */
    useEffect(() => {
        if (!selSumDsId || selSumDsId < 0 || !selectedModelName || selectedModelName.value === '') {
            setModelNos([]);
            return;
        }
        getModelNos({ sumDsId: selSumDsId, modelType: selectedModelType, modelName: selectedModelName })
          .then((data) => {
            const opts = Array.isArray(data) ? data.map((el) => (typeof el === 'string' ? { value: el, label: el } : el)) : [];
            setModelNos(opts);
          })
          .catch(() => setModelNos([]));
    }, [selSumDsId, selectedModelType, selectedModelName]);

    /** 공정 선택 시 센서데이터(범위) 목록 로드 → scope 필터 옵션 제공 */
    useEffect(() => {
        if (!selSumDsId) return;
        getSensorLabels({ sumDsId: selSumDsId })
          .then((data) => {
            console.log('data', data);
            const opts = Array.isArray(data)
              ? data.map((el) => ({
                  value: el.key,
                  label: el.label,
                }))
              : [];
            console.log('opts', opts);
            setSensor(opts);
          })
          .catch((e) => {
            console.error("getSensorLabels failed:", e);
            setSensor([]);
          });
    }, [selSumDsId]);

    /** total을 선택 기간(startDate~endDate)으로 필터 → 차트·요약에 사용 */
    const chartData = useMemo(() => {
        const start = moment(startDate).startOf('day');
        const end = moment(endDate).endOf('day');
        const formats = ['YYYY-MM-DD', 'YY.MM.DD', 'YYYY.MM.DD', 'YYYY/MM/DD', moment.ISO_8601];
      
        const filtered = (total || []).filter((d) => {
          let dateStr = d.date;
          if (dateStr instanceof Date) {
            dateStr = moment(dateStr).format('YYYY-MM-DD');
          } else if (typeof dateStr === 'string' && dateStr.indexOf('T') !== -1) {
            dateStr = dateStr.slice(0, 10);
          } else if (typeof dateStr !== 'string') {
            dateStr = String(dateStr);
          }
          let day = moment(dateStr, formats, true);
          if (!day.isValid()) day = moment(dateStr);
          if (!day.isValid()) return false;
          return day.isSameOrAfter(start) && day.isSameOrBefore(end);
        });
        return filtered.map((d) => {
          let dateStr = d.date;
          if (typeof dateStr === 'string' && dateStr.indexOf('T') !== -1) dateStr = dateStr.slice(0, 10);
          else if (dateStr instanceof Date) dateStr = moment(dateStr).format('YYYY-MM-DD');
          else if (typeof dateStr !== 'string') dateStr = String(dateStr);
          return { ...d, date: dateStr };
        });
      }, [total, startDate, endDate]);


      const labelPolicy = useMemo(() => {
        const len = chartData?.length ?? 0;
        const isDense = len >= LABEL_LIMIT_THRESHOLD;
      
        if (!len) return { isDense, maxIdx: -1 };
      
        // "총생산(count)" 기준으로 최대값 1개 인덱스 선정 (동점이면 가장 먼저 나온 날짜 1개만)
        const maxIdx = chartData.reduce((bestIdx, cur, idx) => {
          const curTotal = Number(cur.count ?? (Number(cur.okCount) + Number(cur.ngCount))) || 0;
          const bestTotal = Number(chartData[bestIdx]?.count ?? (Number(chartData[bestIdx]?.okCount) + Number(chartData[bestIdx]?.ngCount))) || 0;
          return curTotal > bestTotal ? idx : bestIdx;
        }, 0);
      
        return { isDense, maxIdx };
      }, [chartData]);
      

    /** 차종/품명/품번 변경 시 BarChart key로 리마운트 (Recharts 렌더 캐시 방지) */
    const chartDataKey = useMemo(
        () =>
            [selectedModelType?.value, selectedModelName?.value, selectedModelNo?.value]
                .filter(Boolean)
                .join('_') || 'all',
        [selectedModelType, selectedModelName, selectedModelNo]
    );

    useEffect(() => {
        console.log('[차트] chartData 최종값', { length: (chartData || []).length, key: chartDataKey });
    }, [chartData, chartDataKey]);

    /** chartData 기준 요약 통계 (총 생산, 총 NG, 평균 NG율, 최다 NG일 등) */
    const summaryStats = useMemo(() => {
        // 총 생산: 제품 건수(count) 기준, 없으면 okCount+ngCount로 fallback (백엔드/Recharts 호환)
        const totalProduction = (chartData || []).reduce(
            (sum, d) => sum + (Number(d.count ?? (d.okCount + d.ngCount)) || 0),
            0
        );

        // 총 NG: 차트에서 쓰는 ngCount 기준 (scope에 따라 decision/sensor 자동 반영)
        const totalNG = (chartData || []).reduce(
            (sum, d) => sum + (Number(d.ngCount) || 0),
            0
        );

        const avgNGRate =
            totalProduction > 0
                ? ((totalNG / totalProduction) * 100).toFixed(2) + '%'
                : '0%';

        const withRate = (chartData || [])
            .filter((d) => (Number(d.count ?? (d.okCount + d.ngCount)) || 0) > 0)
            .map((d) => {
                const t = Number(d.count ?? (d.okCount + d.ngCount)) || 0;
                const n = Number(d.ngCount) || 0;
                return { date: d.date, rate: t ? (n / t) * 100 : 0 };
            });

        const maxRateRow = withRate.length
            ? withRate.reduce((a, b) => (a.rate >= b.rate ? a : b), { date: '', rate: 0 })
            : null;

        const maxNGRow = (chartData || []).reduce(
            (best, d) => {
                const n = Number(d.ngCount) || 0;
                return !best || n > (Number(best.ngCount) || 0) ? d : best;
            },
            null
        );

        return {
            totalProduction,
            totalNG,
            avgNGRate,
            selectedType: selectedModelType?.label ?? '-',
            selectedName: selectedModelName?.label ?? '-',
            selectedNo: selectedModelNo?.label ?? '-',
            selectedSensor: selectedSensor?.label ?? '-',
            maxNGDate: maxNGRow?.date ?? '-',
            maxNGRate: maxRateRow ? `${maxRateRow.rate.toFixed(2)}%` : '-',
        };
    }, [chartData, selectedModelType, selectedModelName, selectedModelNo, selectedSensor]);

    /** 선택 기간의 일수 (시작일~종료일 양 끝 포함) */
    const selectedDaysCount = useMemo(() => {
        const start = moment(startDate).startOf('day');
        const end = moment(endDate).startOf('day');
        const days = end.diff(start, 'days') + 1;
        return Math.max(0, days);
    }, [startDate, endDate]);

    /** scope 필터 옵션 (전체 / 원자재 / 하중 / 치수) - 센서데이터 드롭다운에서 sensorOptions 사용 시 scope로 변환 */

    /** 차트 Y축 최대값·점선 보조선
     *  - 요구사항: 데이터 크기와 상관없이 항상 "점선 7개, Y축 눈금 7개" 고정
     *  - 구현: 최대값을 기준으로 예쁜(step) 간격을 계산하고 7등분
     */
    const TARGET_REF_LINE_COUNT = 7;
    const chartScale = useMemo(() => {
        const maxVal = !chartData?.length
            ? 0
            : Math.max(...chartData.map((d) => Number(d.count ?? (d.okCount + d.ngCount)) || 0));

        // 데이터가 없으면 간단한 기본 스케일 반환
        if (!maxVal || !Number.isFinite(maxVal)) {
            const step = 1;
            const domainMax = step * TARGET_REF_LINE_COUNT;
            const refLines = Array.from({ length: TARGET_REF_LINE_COUNT }, (_, i) => step * (i + 1));
            const ticks = [0, ...refLines];
            return { domainMax, refLines, ticks };
        }

        // 1) 최대값을 7등분했을 때의 기본 step
        let rawStep = maxVal / TARGET_REF_LINE_COUNT;
        if (rawStep <= 0) rawStep = 1;

        // 2) "예쁜" 숫자로 반올림 (1, 2, 5, 10 * 10^n)
        const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
        const normalized = rawStep / magnitude;
        let niceNorm;
        if (normalized <= 1) niceNorm = 1;
        else if (normalized <= 2) niceNorm = 2;
        else if (normalized <= 5) niceNorm = 5;
        else niceNorm = 10;

        const step = niceNorm * magnitude;

        // 3) Y축 최대값 = step * 7 (항상 점선 7개)
        const domainMax = step * TARGET_REF_LINE_COUNT;

        // 4) 점선 / tick 위치: step, 2*step, ... , 7*step
        const refLines = Array.from({ length: TARGET_REF_LINE_COUNT }, (_, i) => step * (i + 1));
        const ticks = [0, ...refLines];

        return { domainMax, refLines, ticks };
    }, [chartData]);

    /** 날짜 개수에 따른 X축 tick interval
     *  - 원칙 1) 차트 가로폭은 카드 너비 고정 (width: 100%)
     *  - 원칙 2) tick은 최대 10~12개 수준
     *  - 원칙 3) 가능하면 2~3일 간격, 너무 많으면 자동으로 더 띄움
     */
    const chartLayout = useMemo(() => {
        const len = (chartData || []).length;
        if (len <= 0) {
            return { chartWidth: '100%', xAxisInterval: 0 };
        }

        // 1) 데이터가 10개 이하면 전부 표시 (간격 1일)
        if (len <= XAXIS_MAX_VISIBLE_TICKS) {
            return { chartWidth: '100%', xAxisInterval: 0 };
        }

        // 2) 기본 목표: 2일 간격
        const PREFERRED_DAY_INTERVAL = 2; // 2일 간격 기본
        let dayInterval = PREFERRED_DAY_INTERVAL;
        let estimatedTickCount = Math.ceil(len / dayInterval);

        // 3) tick 개수가 10개를 넘으면, tick 개수가 10개 이하가 되도록 dayInterval 자동 증가
        if (estimatedTickCount > XAXIS_MAX_VISIBLE_TICKS) {
            dayInterval = Math.ceil(len / XAXIS_MAX_VISIBLE_TICKS);
            estimatedTickCount = Math.ceil(len / dayInterval);
        }

        // 안전장치: 최소 2일 간격 이상, interval 값은 dayInterval-1
        if (dayInterval < 2) dayInterval = 2;
        const interval = Math.max(1, dayInterval - 1);

        return { chartWidth: '100%', xAxisInterval: interval };
    }, [chartData]);

    /** 적용 버튼: 필터 반영 후 차트 데이터 재조회 */
    const handleApply = (e) => {
        e?.preventDefault?.();
        fetchBarData();
    };

    const handleResetPeriod = (e) => {
        e?.preventDefault?.();
        setSelectPeriod('recent3Days');
        setStartDate(moment().subtract(2, 'days').startOf('day').toDate());
        setEndDate(moment().toDate());
    };

    /* ---- react-select 옵션: 전체 + API 데이터 ---- */
    const modelTypeOptions = useMemo(() => [OPTION_ALL, ...modelTypes.map((t) => (typeof t === 'string' ? { value: t, label: t } : t))], [OPTION_ALL, modelTypes]);
    const modelNameOptions = useMemo(() => {
        if (!selectedModelType || selectedModelType.value === '') return [OPTION_ALL];
        return [OPTION_ALL, ...modelNames.map((n) => (typeof n === 'string' ? { value: n, label: n } : n))];
    }, [OPTION_ALL, modelNames, selectedModelType]);
    const modelNoOptions = useMemo(() => {
        if (!selectedModelName || selectedModelName.value === '') return [OPTION_ALL];
        return [OPTION_ALL, ...modelNos.map((no) => (typeof no === 'string' ? { value: no, label: no } : no))];
    }, [OPTION_ALL, modelNos, selectedModelName]);
    const sensorOptions = useMemo(() => {
        const base = [{ value: "all", label: "전체 선택" }];
        return base.concat(sensor);
    }, [sensor]);

    if (selSumDsId == null || selSumDsId < 0) {
        return <NoData />;
    }

    return (
        <div className="sumDataStatistic">
            <p className="path">데이터 분석<span className="below">{'>'}</span>품질 이력 분석</p>
            <h2 className="pageTitle">품질 이력 분석 - {processDisplayName}</h2>

            {/* 필터 카드: 기간, 공정, 품목(차종/품명/품번), 센서데이터(범위) */}
            <div className="filterCard">
                <form onSubmit={handleApply}>
                    <div className="filterRow filterRow1">
                        <span className="datePickerLabel">기간</span>
                        <div className="datePickerArea">
                            <div className="startDateArea">
                                <CustomDatePickerStart
                                    startDate={startDate}
                                    setStartDate={setStartDate}
                                    endDate={endDate}
                                    changeDropdown={() => setSelectPeriod('input')}
                                />
                            </div>
                            <span>-</span>
                            <div className="endDateArea">
                                <CustomDatePickerEnd
                                    startDate={startDate}
                                    endDate={endDate}
                                    setEndDate={setEndDate}
                                    changeDropdown={() => setSelectPeriod('input')}
                                />
                            </div>
                        </div>
                        <Select
                            options={periodOpt}
                            value={periodOpt.find((el) => el.value === selectPeriod)}
                            onChange={(sel) => setSelectPeriod(sel.value)}
                            isSearchable={false}
                            className="filterSelect periodSelect periodOnly"
                            classNames={{
                                control: (state) => (state.isFocused ? 'selectControl focused' : 'selectControl'),
                                container: () => 'selectBox',
                                menu: () => 'selectMenu',
                                menuList: () => 'optionsList',
                                option: (s) => (s.isSelected ? 'option selected' : s.isFocused ? 'option focused' : 'option'),
                                singleValue: () => 'singleVal',
                                valueContainer: () => 'valueContainer',
                              }}
                            components={{
                                DropdownIndicator: (props) => (
                                  <components.DropdownIndicator {...props}>
                                    <span className={`arrowIcon ${props.isFocused ? 'focused' : ''}`}>
                                      <img src={filledArrowIconImg} alt="드롭다운" />
                                    </span>
                                  </components.DropdownIndicator>
                                ),
                                IndicatorSeparator: () => null,
                              }}
                        />
                        <div className="filterRow1Right">
                            <button type="submit" className="applyBtn">적용</button>
                            <button type="button" className="resetPeriodBtn" onClick={handleResetPeriod}>
                                <span>기간 초기화</span>
                                <img src={resetIconImg} alt="초기화" />
                            </button>
                        </div>
                    </div>
                    <div className="filterRow filterRow2">
                        <span className="filterLabel">품목</span>
                        <div className="selectGroup">
                            <div className="selectItem">
                                <span className="selectItemLabel">차종</span>
                                <Select
                                    options={modelTypeOptions}
                                    value={selectedModelType}
                                    onChange={(v) => {
                                        console.log('[차트] 차종 변경', v?.value ?? v);
                                        setSelectedModelType(v);
                                        setSelectedModelName(OPTION_ALL);
                                        setSelectedModelNo(OPTION_ALL);
                                    }}
                                    placeholder="전체 선택"
                                    isSearchable={false}
                                    classNames={{
                                        control: () => 'selectControl',
                                        container: () => 'selectBox',
                                        menu: () => 'selectMenu',
                                        option: (s) => s.isSelected ? 'option selected' : s.isFocused ? 'option focused' : 'option',
                                        singleValue: () => 'singleVal',
                                        valueContainer: () => 'valueContainer',
                                        placeholder: () => 'selectPlaceholder',
                                    }}
                                    components={{
                                        DropdownIndicator: (s) => (
                                            <span className={s.isFocused ? 'arrowIcon focused' : 'arrowIcon'}>
                                                <img src={filledArrowIconImg} alt="드롭다운" />
                                            </span>
                                        ),
                                        IndicatorSeparator: () => null,
                                    }}
                                />
                            </div>
                            <div className="selectItem">
                                <span className="selectItemLabel">품명</span>
                                <Select
                                    options={modelNameOptions}
                                    value={selectedModelName}
                                    onChange={(v) => {
                                        setSelectedModelName(v);
                                        setSelectedModelNo(OPTION_ALL);
                                    }}
                                    placeholder="전체 선택"
                                    isSearchable={false}
                                    isDisabled={!selectedModelType || selectedModelType.value === ''}
                                    classNames={{
                                        control: (state) => state.isDisabled ? 'selectControl selectControl--disabled' : 'selectControl',
                                        container: () => 'selectBox',
                                        menu: () => 'selectMenu',
                                        option: (s) => s.isSelected ? 'option selected' : s.isFocused ? 'option focused' : 'option',
                                        singleValue: () => 'singleVal',
                                        valueContainer: () => 'valueContainer',
                                        placeholder: () => 'selectPlaceholder',
                                    }}
                                    components={{
                                        DropdownIndicator: (props) => (
                                            <components.DropdownIndicator {...props}>
                                                <span className={`arrowIcon ${props.isFocused ? 'focused' : ''}`}>
                                                    <img src={filledArrowIconImg} alt="드롭다운" />
                                                </span>
                                            </components.DropdownIndicator>
                                        ),
                                        IndicatorSeparator: () => null,
                                    }}
                                />
                            </div>
                            <div className="selectItem">
                                <span className="selectItemLabel">품번</span>
                                <Select
                                    options={modelNoOptions}
                                    value={selectedModelNo}
                                    onChange={(v) => setSelectedModelNo(v)}
                                    placeholder="전체 선택"
                                    isSearchable={false}
                                    isDisabled={!selectedModelName || selectedModelName.value === ''}
                                    classNames={{
                                        control: (state) => state.isDisabled ? 'selectControl selectControl--disabled' : 'selectControl',
                                        container: () => 'selectBox',
                                        menu: () => 'selectMenu',
                                        menuList: () => 'optionsList',
                                        option: (s) => s.isSelected ? 'option selected' : s.isFocused ? 'option focused' : 'option',
                                        singleValue: () => 'singleVal',
                                        valueContainer: () => 'valueContainer',
                                        placeholder: () => 'selectPlaceholder',
                                    }}
                                    components={{
                                        DropdownIndicator: (props) => (
                                            <components.DropdownIndicator {...props}>
                                                <span className={`arrowIcon ${props.isFocused ? 'focused' : ''}`}>
                                                    <img src={filledArrowIconImg} alt="드롭다운" />
                                                </span>
                                            </components.DropdownIndicator>
                                        ),
                                        IndicatorSeparator: () => null,
                                    }}
                                />
                            </div>
                            <div className="selectItem sensorBlock">
                                <span className="selectItemLabel sensorLabel">센서</span>
                                <Select
                                    options={sensorOptions}
                                    value={selectedSensor}
                                    onChange={(v) => {
                                        setSelectedSensor(v);
                                        // 선택 해제 등으로 값이 falsy면 항상 "all"로 복귀 → 전체 NG 기준
                                        setScope(v?.value || 'all');
                                    }}
                                    placeholder="전체 선택"
                                    isSearchable={false}
                                    classNames={{
                                        control: () => 'selectControl',
                                        container: () => 'selectBox',
                                        menu: () => 'selectMenu',
                                        menuList: () => 'optionsList',
                                        option: (s) => s.isSelected ? 'option selected' : s.isFocused ? 'option focused' : 'option',
                                        singleValue: () => 'singleVal',
                                        valueContainer: () => 'valueContainer',
                                        placeholder: () => 'selectPlaceholder',
                                    }}
                                    components={{
                                        DropdownIndicator: (props) => (
                                            <components.DropdownIndicator {...props}>
                                                <span className={`arrowIcon ${props.isFocused ? 'focused' : ''}`}>
                                                    <img src={filledArrowIconImg} alt="드롭다운" />
                                                </span>
                                            </components.DropdownIndicator>
                                        ),
                                        IndicatorSeparator: () => null,
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </form>
            </div>

            {/* 생산/NG 막대 차트 - 가로폭은 카드 너비 고정, 날짜 많으면 축 안에서만 압축 */}
            <div className="chartCard">
                <div className="chartWrapper">
                    {isLoadingChart ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: CHART_HEIGHT, color: '#888', fontSize: '14px' }}>
                            로딩 중...
                        </div>
                    ) : chartData && chartData.length > 0 ? (
                        <div className="chartInnerWrap">
                            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                                <BarChart
                                    key={chartDataKey}
                                    data={chartData}
                                    margin={{ top: 30, right: 30, left: 26, bottom: 5 }}
                                    barCategoryGap={`${BAR_CATEGORY_GAP_PERCENT}%`}
                                    barGap={BAR_GAP_PERCENT}
                                    barSize={Math.min(48, Math.max(24, BAR_WIDTH_PER_ITEM_PX - 12))}
                                >
                                    <XAxis
                                        dataKey="date"
                                        axisLine={{ stroke: '#000000' }}
                                        interval={chartLayout.xAxisInterval}
                                        angle={0}
                                        textAnchor="middle"
                                        height={56}
                                        tick={{ fontFamily: 'NanumSquare', fontSize: XAXIS_TICK_FONT_SIZE, fill: '#4D4D4D' }}
                                        tickFormatter={(val) => (val && typeof val === 'string' ? val.split('T')[0].replace(/-/g, '.') : val)}
                                        label={(props) => {
                                            const { viewBox } = props;
                                            if (!viewBox || viewBox.width == null) return null;
                                            return (
                                                <text
                                                    x={viewBox.x + viewBox.width }
                                                    y={viewBox.y + viewBox.height-15}
                                                    textAnchor="middle"
                                                    fill="#000"
                                                    fontFamily="NanumSquare"
                                                    fontSize={12}
                                                >
                                                    일자
                                                </text>
                                            );
                                        }}
                                    />
                                    <YAxis
                                        domain={[0, chartScale.domainMax]}
                                        ticks={chartScale.ticks}
                                        axisLine={{ stroke: '#000000' }}
                                        tick={{ fontFamily: 'NanumSquare, sans-serif', fontSize: 12, fill: '#4D4D4D' }}
                                        tickFormatter={(v) => v.toLocaleString()}
                                        label={{ value: '생산 및 품질 결과 (개)', angle: -90, position: 'left', style: { fontFamily: 'NanumSquare, sans-serif', fontSize: 13, fill: '#000', textAnchor: 'middle' } }}
                                    />
                                    <Tooltip
                                        content={<ChartTooltipContent />}
                                        contentStyle={{ fontFamily: 'NanumSquare, sans-serif' }}
                                    />
                                    {chartScale.refLines.map((y) => (
                                        <ReferenceLine
                                            key={y}
                                            y={y}
                                            stroke="rgba(10, 43, 131, 0.38)"
                                            strokeDasharray="4 4"
                                            strokeWidth={1}
                                        />
                                    ))}
                                    <Legend
                                        wrapperStyle={{ paddingTop: 16 }}
                                        formatter={(value) => <span className="legendLabel">{value}</span>}
                                    />
                                    {/* 파란: OK, 주황: NG (스택 합 = 총 생산) */}
                                    <Bar dataKey="okCount" name="OK" stackId="a" fill="#0A2B83" radius={[0, 0, 0, 0]}>
                                        <LabelList dataKey="okCount" content={(props) => {
                                            const idx = props.index ?? -1;
                                            const row = chartData?.[idx];

                                            // 30개 이상이면 max 1개만
                                            if (labelPolicy.isDense && idx !== labelPolicy.maxIdx) return null;

                                            // NG가 있으면 top segment는 NG라서 OK엔 라벨 붙이면 위치가 어색할 수 있음 -> NG가 0일 때만 OK에 Total 라벨
                                            if (Number(row?.ngCount) > 0) return null;

                                            // 20개 이상이어도 TotalLabelOnTopSegment 사용 (OK/NG 높이 비교 로직 동일 적용)
                                            return <TotalLabelOnTopSegment {...props} payload={props.payload ?? row} chartHeight={CHART_HEIGHT} />;
                                        }} />
                                    </Bar>
                                    <Bar dataKey="ngCount" name="NG" stackId="a" fill="#C04F15" radius={[0, 0, 0, 0]}>
                                        <LabelList dataKey="ngCount" content={(props) => {
                                            const idx = props.index ?? -1;
                                            const row = chartData?.[idx];

                                            // 30개 이상이면 max 1개만
                                            if (labelPolicy.isDense && idx !== labelPolicy.maxIdx) return null;

                                            // NG가 있을 때만(스택 top) 여기에서 Total 라벨을 붙임
                                            if (Number(row?.ngCount) <= 0) return null;

                                            // 20개 이상이어도 TotalLabelOnTopSegment 사용 (OK/NG 높이 비교 로직 동일 적용)
                                            return <TotalLabelOnTopSegment {...props} payload={props.payload ?? row} value={props.value} chartHeight={CHART_HEIGHT} />;
                                        }} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: CHART_HEIGHT,
                            color: '#888',
                            fontSize: '14px',
                        }}>
                            {!selectedModelType ? '차종을 먼저 선택해 주세요.' : '선택한 기간에 데이터가 없습니다.'}
                        </div>
                    )}
                </div>
            </div>

            {/* 요약 통계 카드 (이미지 기준: 키·값 간격·폰트·라운드) */}
            <div className="summaryCard">
                <ul className="summaryList">
                    <li><span className="summaryKey">ᆞ총 선택 일자</span><span className="summaryColon"> : </span><span className="summaryVal">{selectedDaysCount}일</span></li>
                    <li><span className="summaryKey">ᆞ선택 차종</span><span className="summaryColon"> : </span><span className="summaryVal">{summaryStats.selectedType}</span></li>
                    <li><span className="summaryKey">ᆞ품명</span><span className="summaryColon"> : </span><span className="summaryVal">{summaryStats.selectedName}</span></li>
                    <li><span className="summaryKey">ᆞ품번</span><span className="summaryColon"> : </span><span className="summaryVal">{summaryStats.selectedNo}</span></li>
                    <li><span className="summaryKey">ᆞ선택 센서</span><span className="summaryColon"> : </span><span className="summaryVal">{summaryStats.selectedSensor}</span></li>
                    <li><span className="summaryKey">ᆞ총 생산 수량</span><span className="summaryColon"> : </span><span className="summaryVal">{summaryStats.totalProduction.toLocaleString()}개</span></li>
                    <li><span className="summaryKey">ᆞ총 NG 발생 건수</span><span className="summaryColon"> : </span><span className="summaryVal">{summaryStats.totalNG.toLocaleString()}개</span></li>
                    <li><span className="summaryKey">ᆞ평균 NG 발생률</span><span className="summaryColon"> : </span><span className="summaryVal">{summaryStats.avgNGRate}</span></li>
                    <li><span className="summaryKey">ᆞ최다 NG 발생일</span><span className="summaryColon"> : </span><span className="summaryVal">{summaryStats.maxNGDate}</span></li>
                    <li><span className="summaryKey">ᆞ최고 NG 발생률</span><span className="summaryColon"> : </span><span className="summaryVal">{summaryStats.maxNGRate}</span></li>
                </ul>
            </div>
        </div>
    );
}

export default SumDataStatistic;
